import cn from 'classnames';
import type { UploadedFile } from 'models';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Form, Icon, type RichTextEditorBase, RichTextInput, type RichTextMentions, message } from 'ui';
import { useUser } from 'features/auth';
import { AttachmentInput } from './attachment-input';
import { FileAttachments } from './file-attachments';

export type CommentFormValues = {
  attachments: { files: UploadedFile[]; loading: boolean };
  htmlContent: string;
  textContent: string;
};

type P = {
  avatar?: boolean;
  editorRef?: RefObject<null | RichTextEditorBase>;
  initialValues?: Partial<CommentFormValues>;
  isLoading?: boolean;
  onRequestMentions: RichTextMentions;
  onSubmit: (values: CommentFormValues) => void;
};

const CommentForm = ({ avatar = true, editorRef, initialValues, isLoading, onRequestMentions, onSubmit }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CommentFormValues>();

  const { railsUserData } = useUser();

  const formInitialValues: Partial<CommentFormValues> = {
    attachments: { files: [], loading: false },
    htmlContent: '',
    textContent: '',
    ...initialValues,
  };

  return (
    <Form<CommentFormValues>
      className={cn('comment-form', { 'comment-form--no-user': !avatar })}
      form={form}
      initialValues={formInitialValues}
      onFinish={onSubmit}
    >
      <Form.Item<CommentFormValues>
        noStyle
        shouldUpdate={(previous, current) => previous.attachments !== current.attachments}
      >
        {({ getFieldsValue, setFieldsValue }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          const { attachments } = getFieldsValue(true) as CommentFormValues;

          if (attachments.files.length > 0) {
            return (
              <div className="comment-form__attachments">
                <FileAttachments
                  attachments={attachments.files}
                  onClear={() => {
                    setFieldsValue({ attachments: { files: [], loading: attachments.loading } });
                  }}
                  onRemove={(attachment) => {
                    setFieldsValue({
                      attachments: {
                        files: attachments.files.filter((file) => file !== attachment),
                        loading: attachments.loading,
                      },
                    });
                  }}
                />
              </div>
            );
          }

          return null;
        }}
      </Form.Item>

      {avatar ? (
        <div className="comment-form__user">
          <Avatar photo={railsUserData.profilePhoto?.url} size="default" />
        </div>
      ) : null}

      <div className="comment-form__rich-text">
        <RichTextInput
          html={{
            dependencies: ['attachments'],
            editorRef,
            height: 'small',
            isFocusedInitially: true,
            label: 'Comment',
            mentions: onRequestMentions,
            name: 'htmlContent',
            noOptional: true,
            placeholder: 'Write comment...',
            rules: [
              {
                validateTrigger: ['onSubmit'],
                // eslint-disable-next-line @typescript-eslint/require-await -- we need return promise with async function
                async validator(_, value) {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  const attachments = form.getFieldValue('attachments');

                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  if ((!attachments.files || attachments.files.length === 0) && !value) {
                    throw new Error(t('Error: Post cannot be empty'));
                  }
                },
              },
            ],
          }}
          text={{ name: 'textContent' }}
        />
      </div>

      <div className="comment-form__actions">
        <Form.Item<CommentFormValues>
          name="attachments"
          noStyle
          rules={[
            {
              message: '',
              validateTrigger: ['onSubmit'],
              // eslint-disable-next-line @typescript-eslint/require-await -- we need return promise with async function
              async validator(_, attachments) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                if (attachments.loading) {
                  message.error(
                    'The attachment upload is still in progress. You can submit the post after the upload is finished.',
                  );
                  throw new Error(t('Error: The attachment upload is still in progress.'));
                }
              },
            },
          ]}
        >
          <AttachmentInput className="comment-form__actions__attachments" />
        </Form.Item>

        <Button
          aria-label="Post"
          htmlType="submit"
          icon={<Icon icon="send-line" />}
          loading={isLoading}
          size="large"
          type="primary"
        />
      </div>
    </Form>
  );
};

export default CommentForm;
